<script setup lang="ts">
/**
 * ScanInput
 *
 * A drop-in replacement for `<InputText>` that adds a barcode scan button.
 *
 * Drop-in usage — any form in the application:
 *   1. Create one scanner instance per page/dialog:
 *        const scanner = useBarcodeScanner({ workflow: 'my-workflow' });
 *        // connect on open, disconnect on close
 *
 *   2. Replace any InputText that should be scannable:
 *        <!-- Before -->
 *        <InputText v-model="containerId" placeholder="Scan or type…" />
 *
 *        <!-- After -->
 *        <ScanInput v-model="containerId" :scanner="scanner" placeholder="Scan or type…" />
 *
 * Each ScanInput manages its own per-field scan status independently.
 * Multiple ScanInput components on the same form each show their own
 * status chip without interfering with each other.
 *
 * Props forwarded to the underlying InputText:
 *   placeholder, maxlength, disabled, class, data-testid, and any
 *   standard HTML input attributes via $attrs.
 */
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import InputText from 'primevue/inputtext';
import { Icon } from '@tms-phoenix/ui-vue';
import type { ScannerUiStatus } from '@/shared/composables/useScannerRequestCoordinator';

// ── Minimal structural type for the scanner prop ────────────────────────────
// Using a structural (duck-type) interface instead of ReturnType<typeof useBarcodeScanner>
// so that ScanInput remains decoupled from the exact composable implementation.
type ScannerProp = {
  isScannerConnected: { readonly value: boolean };
  scannerError: { readonly value: string | null };
  triggerScanInto: (
    target: { value: string },
    onStatus: (status: ScannerUiStatus) => void,
  ) => Promise<void>;
};

// ── Props / emits ──────────────────────────────────────────────────────────

interface Props {
  /** The shared scanner instance from `useBarcodeScanner()` in the parent. */
  scanner: ScannerProp;
  /** Placeholder text forwarded to InputText. */
  placeholder?: string;
  /** Maximum character length forwarded to InputText. */
  maxlength?: number | string;
  /** Disable both the input and the scan button. */
  disabled?: boolean;
  /** Mark the input as invalid (PrimeVue `p-invalid` class). */
  invalid?: boolean;
  /** Extra CSS classes applied to the outer wrapper div. */
  wrapperClass?: string;
  /**
   * Auto-focus the native input on mount. Defaults to true. Set to `false` on every
   * ScanInput but the intended first field when a form/dialog has more than one —
   * each ScanInput focuses itself independently on mount, so with multiple instances
   * the last one to mount wins the race and steals focus from the intended field.
   */
  autofocus?: boolean;
}

// Disable automatic attr inheritance so $attrs (including event listeners like
// @keyup.enter) are forwarded exclusively to the inner InputText via v-bind="$attrs",
// preventing double-firing on the root wrapper div.
defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Scan or type…',
  maxlength: 100,
  disabled: false,
  invalid: false,
  wrapperClass: '',
  autofocus: true,
});

/** The field value — two-way bound by the parent via `v-model`. */
const model = defineModel<string>({ required: true });

// ── Emits ──────────────────────────────────────────────────────────────────
const emit = defineEmits<{
  /**
   * Fired with the trimmed, non-empty value when the user presses Enter, when a
   * scanner-speed burst of keystrokes settles, or (debounced) when the value is
   * set from code. Never fired while a person is still typing.
   */
  scan: [value: string];
}>();

// ── Template ref for auto-focus ────────────────────────────────────────────
const inputRef = ref<InstanceType<typeof InputText> | null>(null);

// Auto-focus the native input when the component mounts (dialog open / page render).
onMounted(() => {
  if (!props.autofocus) return;
  nextTick(() => {
    focus();
  });
});

/** Focus the native input — exposed so parents can refocus after clearing the field (e.g. between successive scans). */
function focus() {
  const el = (inputRef.value as unknown as { $el?: HTMLInputElement })?.$el;
  el?.focus();
}

defineExpose({ focus });

// ── When does `scan` fire? ─────────────────────────────────────────────────
// A person typing must never trigger a search mid-word (SV-464) — only Enter (or
// the parent's Search button) does. A barcode scanner types the whole code at
// machine speed, usually followed by Enter; both are covered:
//   • Enter                         → emit immediately.
//   • A fast burst of keystrokes    → treated as a scan; emit once it settles, for
//                                     scanners that don't send Enter.
//   • Value set from code (scan result pushed by the scanner service, a prefilled
//     MRN, …)                       → emit after a short debounce, as before.
// Thresholds are best estimates for wedge-style scanners — tune on a real device.
const SCAN_MAX_KEY_GAP_MS = 50;
const SCAN_MIN_BURST_CHARS = 4;
const SCAN_SETTLE_MS = 150;
const PROGRAMMATIC_DEBOUNCE_MS = 300;

let lastKeyAt = 0;
let burstChars = 0;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
/** True from a native `input` event until the end of that task, so the value watcher can tell typing from code. */
let userEditing = false;

function clearSettleTimer() {
  if (settleTimer !== null) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
}

function resetBurst() {
  clearSettleTimer();
  burstChars = 0;
  lastKeyAt = 0;
}

function emitScan() {
  const trimmed = model.value.trim();
  if (trimmed) emit('scan', trimmed);
}

// Both handlers run in the capture phase so they see the event before the input
// updates `model` and the watcher below runs.
function onKeydown(event: KeyboardEvent) {
  if (event.isComposing) return;
  if (event.key === 'Enter') {
    event.preventDefault();
    resetBurst();
    emitScan();
    return;
  }
  if (event.key === 'Backspace' || event.key === 'Delete') {
    resetBurst();
    return;
  }
  // Only plain printable characters count — modifiers such as Shift (sent by some
  // scanners around capitals) must not break a burst.
  if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

  // event.timeStamp is when the key was pressed, not when this handler ran, so a busy
  // main thread can't make slow typing look like a scan.
  burstChars = lastKeyAt && event.timeStamp - lastKeyAt <= SCAN_MAX_KEY_GAP_MS ? burstChars + 1 : 1;
  lastKeyAt = event.timeStamp;
  clearSettleTimer();
  if (burstChars >= SCAN_MIN_BURST_CHARS) {
    settleTimer = setTimeout(() => {
      settleTimer = null;
      burstChars = 0;
      emitScan();
    }, SCAN_SETTLE_MS);
  }
}

function onInput(event: Event) {
  userEditing = true;
  setTimeout(() => { userEditing = false; }, 0);
  // Pasted/dropped text arrives in one go with no key timing — it waits for Enter / Search.
  const inputType = (event as InputEvent).inputType;
  if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') resetBurst();
}

onBeforeUnmount(clearSettleTimer);

watch(model, (val, _old, onCleanup) => {
  // Typed or pasted: handled by onKeydown / Enter / the parent's Search button.
  if (userEditing) return;
  const trimmed = val.trim();
  const timer = setTimeout(() => {
    if (trimmed) {
      emit('scan', trimmed);
    }
  }, PROGRAMMATIC_DEBOUNCE_MS);
  onCleanup(() => clearTimeout(timer));
});

// ── Per-field scan status (independent per ScanInput instance) ─────────────

const scanStatus = ref<ScannerUiStatus>('Idle');
let resetTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleReset() {
  if (resetTimer !== null) {
    clearTimeout(resetTimer);
  }
  resetTimer = setTimeout(() => {
    resetTimer = null;
    // Only reset if a new scan hasn't already started
    if (scanStatus.value !== 'Scanning') {
      scanStatus.value = 'Idle';
    }
  }, 3000);
}

async function handleScanClick() {
  if (props.disabled || scanStatus.value === 'Scanning') return;
  // Cancel any pending auto-reset before starting a new scan
  if (resetTimer !== null) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
  await props.scanner.triggerScanInto(model, (status) => {
    scanStatus.value = status;
    // Auto-reset to Idle after a short delay on terminal states
    if (status === 'Decoded' || status === 'NoBarcode' || status === 'ScannerBusy' || status === 'Failed') {
      scheduleReset();
    }
  });
}
</script>

<template>
  <div
    :class="['flex flex-col gap-0.5', wrapperClass]"
    @keydown.capture="onKeydown"
    @input.capture="onInput"
  >
    <!-- Input row -->
    <div class="relative flex items-center">
      <!-- Leading QR icon inside the input -->
      <span class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <Icon
          name="QrCodeScanner"
          size-class="h-4 w-4 shrink-0"
          :color="disabled ? 'text-gray-300' : 'text-gray-400'"
        />
      </span>

      <!-- Text input -->
      <InputText
        ref="inputRef"
        v-model="model"
        v-bind="$attrs"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :disabled="disabled"
        class="w-full pl-9 pr-10"
        :class="{ 'p-invalid': invalid }"
      />

      <!-- Scan trigger button (trailing icon inside input) — hidden, kept for future use -->
      <button
        v-show="false"
        type="button"
        :disabled="disabled || scanStatus === 'Scanning'"
        class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-6 w-6 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        :title="scanner.isScannerConnected.value
          ? 'Click to scan barcode'
          : 'Connecting to scanner…'"
        @click.prevent="handleScanClick"
      >
        <!-- Scanning spinner -->
        <span
          v-if="scanStatus === 'Scanning'"
          class="inline-block h-3.5 w-3.5 rounded-full border-2 border-primary-400 border-t-transparent animate-spin"
        />
        <!-- Decoded checkmark -->
        <Icon
          v-else-if="scanStatus === 'Decoded'"
          name="CircleCheck"
          size-class="h-4 w-4"
          color="text-green-500"
          variant="stroke"
        />
        <!-- Scan trigger icon (default) -->
        <span class="px-2 py-0 bg-gray-100 rounded mr-4">
          scan
        </span>
      </button>
    </div>

    <!-- Inline per-field status line -->
    <div
      v-if="scanStatus !== 'Idle'"
      class="flex items-center gap-1.5 text-xs px-0.5"
      role="status"
      aria-live="polite"
    >
      <span
        v-if="scanStatus === 'Scanning'"
        class="inline-flex items-center gap-1 text-primary-600"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" />
        {{ $t('ui.scan.scanning') }}
      </span>
      <span
        v-else-if="scanStatus === 'Decoded'"
        class="inline-flex items-center gap-1 text-green-600"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-green-500" />
        {{ $t('ui.scan.decoded') }}
      </span>
      <span
        v-else-if="scanStatus === 'NoBarcode'"
        class="inline-flex items-center gap-1 text-yellow-600"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-yellow-400" />
        {{ $t('ui.scan.notFound') }}
      </span>
      <span
        v-else-if="scanStatus === 'ScannerBusy'"
        class="inline-flex items-center gap-1 text-yellow-600"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-yellow-400" />
        {{ $t('ui.scan.busy') }}
      </span>
      <span
        v-else-if="scanStatus === 'Failed'"
        class="inline-flex items-center gap-1 text-red-500"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-red-400" />
        {{ $t('ui.scan.failed') }}
      </span>
    </div>
  </div>
</template>
